import { Agent, Channel, MerchantStructure, PaymentMethodType } from '@nemuru-eng/js-api-client';

import { ApplicationType } from '@/config/types';
import { MerchantInStructure } from '@/types/MerchantInStructure';
import { getApplicationTypes } from '@/utils/getApplicationTypes';

export default class MerchantStructureEntity {
  constructor(
    private readonly merchantStructure: MerchantStructure,
    private readonly channelId?: string,
    private readonly agentId?: string,
  ) {}

  public static create({
    merchantStructure,
    channelId,
    agentId,
  }: {
    merchantStructure: MerchantStructure;
    channelId?: string;
    agentId?: string;
  }): MerchantStructureEntity {
    return new MerchantStructureEntity(merchantStructure, channelId, agentId);
  }

  public getMerchant(): MerchantInStructure {
    return this.merchantStructure.merchant;
  }

  public getChannel(): Channel | undefined {
    // The channel of the corresponding agent
    if (this.agentId) {
      return this.merchantStructure.merchant.channels.find((channel) =>
        channel.agents.some((agent) => agent.id === this.agentId),
      );
    }

    // The channel
    if (this.channelId) {
      return this.merchantStructure.merchant.channels.find((channel) => channel.id === this.channelId);
    }
  }

  public getChannelOrFirstChannel(): Channel {
    let channel: Channel | undefined = this.getChannel();

    // The first channel that has agents
    if (!this.agentId && !this.channelId) {
      channel = this.merchantStructure.merchant.channels.find((channel) => channel.agents.length > 0);
    }

    if (!channel) {
      throw new Error('Channel not found');
    }

    return channel;
  }

  public getAgent(): Agent | undefined {
    let agent: Agent | undefined;

    // The agent
    if (this.agentId) {
      this.merchantStructure.merchant.channels.find((channel) => {
        const match = channel.agents.find((agent) => agent.id === this.agentId);
        if (match) {
          agent = match;
          return true;
        }
      });
      return agent;
    }
  }

  public getAgentOrFirstAgent(): Agent {
    let agent: Agent | undefined = this.getAgent();

    // The first agent of the channel that has agents
    if (!this.agentId) {
      agent = this.getChannelOrFirstChannel().agents[0];
    }

    if (!agent) {
      throw new Error('Agent not found');
    }

    return agent;
  }

  private getAgentList(): Agent[] {
    let agentList: Agent[] = [];

    // The agent
    if (this.agentId) {
      const agent = this.getAgent();
      agentList = agent ? [agent] : [];
    }
    // The channel agents
    if (!this.agentId && this.channelId) {
      agentList = this.getChannel()?.agents ?? [];
    }
    // The sum of agents of all channels
    if (!this.agentId && !this.channelId) {
      agentList = this.merchantStructure.merchant.channels.flatMap((channel) => channel.agents);
    }

    return agentList;
  }

  public getAllowedProducts(): ApplicationType[] {
    const allowedProducts = new Set<ApplicationType>();

    this.getAgentList().forEach((agent) => {
      const agentAllowedProducts = getApplicationTypes({
        assetTypes: agent.allowedAssetTypes,
        onboardingFulfilledBys: agent.allowedOnboardingFulfilledByTypes,
        allowedPaymentMethods: agent.allowedPaymentMethodTypes,
      });

      agentAllowedProducts.forEach((applicationType) => {
        allowedProducts.add(applicationType);
      });
    });

    return [...allowedProducts];
  }

  public getAllowedPaymentMethodsByUserType(): PaymentMethodType[] {
    const allPaymentMethodTypes = Object.values(PaymentMethodType).map((value) => value);
    if (this.agentId) {
      return [
        ...new Set(allPaymentMethodTypes.filter((value) => this.getAgent()?.allowedPaymentMethodTypes.includes(value))),
      ];
    }
    if (this.channelId) {
      return [
        ...new Set(
          allPaymentMethodTypes.filter((value) =>
            this.getChannel()?.agents.some((agent) => agent.allowedPaymentMethodTypes.includes(value)),
          ),
        ),
      ];
    }
    return [
      ...new Set(
        allPaymentMethodTypes.filter((value) =>
          this.merchantStructure.merchant.channels.some((channel) =>
            channel?.agents.some((agent) => agent.allowedPaymentMethodTypes.includes(value)),
          ),
        ),
      ),
    ];
  }

  static getSortedStructure(merchantStructure: MerchantStructure): MerchantStructure {
    return {
      ...merchantStructure,
      merchant: {
        ...merchantStructure.merchant,
        channels: merchantStructure.merchant.channels
          .sort((a, b) => (a.description < b.description ? -1 : 1))
          .map((channel) => ({ ...channel, agents: channel.agents.sort((a, b) => (a.name < b.name ? -1 : 1)) })),
      },
    };
  }
}
